import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { ELocalStorage } from '../../enums';

export interface StaffUser {
  _id: string;
  name: string;
  email: string;
  role: 'owner' | 'staff';
  is_active: boolean;
}

interface LoginTokenResponse {
  // Platform API-user token — required on every secured request as x-am-authorization,
  // independent of the DB-auth end-user token below (confirmed against the platform's
  // own Request Headers Reference: "x-am-authorization ... required for secured APIs").
  api_token: string;
  // DB-auth end-user token — sent as x-am-user-authorization.
  token: string;
  refresh_token: string;
  expires_in: number;
}

interface LoginResponse extends LoginTokenResponse {
  user: StaffUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);

  private readonly apiToken = signal<string | null>(localStorage.getItem(ELocalStorage.API_TOKEN));
  private readonly accessToken = signal<string | null>(localStorage.getItem(ELocalStorage.ACCESS_TOKEN));
  private readonly refreshTokenValue = signal<string | null>(localStorage.getItem(ELocalStorage.REFRESH_TOKEN));
  private readonly currentUser = signal<StaffUser | null>(this.readStoredUser());

  readonly user = this.currentUser.asReadonly();
  readonly isAuthenticated = computed(() => !!this.apiToken() && !!this.accessToken() && !!this.currentUser());

  // Owner-only affordances (discounts, order cancellation, invoice settings) are enforced
  // server-side by requireOwner — this signal only decides whether to SHOW them. Hiding a
  // control a staff user cannot use is a UX concern, not the security boundary: a tampered
  // localStorage role flips this to true and still gets rejected by the backend.
  readonly isOwner = computed(() => this.currentUser()?.role === 'owner');

  private readStoredUser(): StaffUser | null {
    const raw = localStorage.getItem(ELocalStorage.STAFF_USER);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StaffUser;
    } catch {
      return null;
    }
  }

  private persistSession(session: LoginTokenResponse, user?: StaffUser): void {
    // The backend throws if either token exchange fails, so a response reaching here
    // missing a field means the platform's success envelope didn't carry what the
    // handler returned (or the deployed handler is out of sync with source) — fail
    // loudly instead of writing the string "undefined" to localStorage/signals, which
    // otherwise leaves isAuthenticated() permanently false with no visible error.
    if (!session.api_token || !session.token || !session.refresh_token) {
      throw new Error('Login response is missing required tokens. Please try again.');
    }
    localStorage.setItem(ELocalStorage.API_TOKEN, session.api_token);
    localStorage.setItem(ELocalStorage.ACCESS_TOKEN, session.token);
    localStorage.setItem(ELocalStorage.REFRESH_TOKEN, session.refresh_token);
    this.apiToken.set(session.api_token);
    this.accessToken.set(session.token);
    this.refreshTokenValue.set(session.refresh_token);
    if (user) {
      localStorage.setItem(ELocalStorage.STAFF_USER, JSON.stringify(user));
      this.currentUser.set(user);
    }
  }

  getApiToken(): string | null {
    return this.apiToken();
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  getRefreshToken(): string | null {
    return this.refreshTokenValue();
  }

  async login(email: string, password: string): Promise<StaffUser> {
    const response = await firstValueFrom(
      this.api.callCustomApi<LoginResponse>('login', 'POST', { email, password }),
    );
    this.persistSession(response, response.user);
    return response.user;
  }

  // Called by the auth interceptor when a request 401s — exchanges the stored refresh
  // token for a fresh access token (and a reissued API-user token) without asking the
  // user to log in again. Returns false (rather than throwing) on failure so the
  // interceptor can fall back to a real logout without a second layer of error handling.
  async refreshAccessToken(): Promise<boolean> {
    const currentRefreshToken = this.refreshTokenValue();
    if (!currentRefreshToken) return false;

    try {
      const response = await firstValueFrom(
        this.api.callCustomApi<LoginTokenResponse>('refresh-token', 'POST', {
          refresh_token: currentRefreshToken,
        }),
      );
      this.persistSession(response);
      return true;
    } catch {
      return false;
    }
  }

  logout(): void {
    localStorage.removeItem(ELocalStorage.API_TOKEN);
    localStorage.removeItem(ELocalStorage.ACCESS_TOKEN);
    localStorage.removeItem(ELocalStorage.REFRESH_TOKEN);
    localStorage.removeItem(ELocalStorage.STAFF_USER);
    this.apiToken.set(null);
    this.accessToken.set(null);
    this.refreshTokenValue.set(null);
    this.currentUser.set(null);
  }
}
